import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Phone, ChevronLeft, ChevronRight, ChevronDown, Menu, X, Search, Store } from "lucide-react";
import { api, fileUrl, trackListingView } from "@/lib/api";
import { QuickEstimator } from "@/components/QuickEstimator";
import { ListingCard } from "@/components/ListingCard";
import { Wizard } from "@/components/wizard/Wizard";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const DEFAULT_HERO = "/api/files/iphone-buyback/uploads/hero.png";
const LOGO = "/api/files/iphone-buyback/uploads/2d203b64-bd55-4990-8030-4cb98e0a08fc.png";

const AppleLogo = ({ className }) => (
  <svg viewBox="0 0 384 512" className={className} fill="currentColor" aria-hidden="true">
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zM262.1 104.5c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
  </svg>
);

export default function Home() {
  const [open, setOpen] = useState(false);
  const [initial, setInitial] = useState(null);
  const [wkey, setWkey] = useState(0);
  const [howOpen, setHowOpen] = useState(false);
  const [faqOpen, setFaqOpen] = useState(false);
  const [faqIdx, setFaqIdx] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [pageSize, setPageSize] = useState(() => (typeof window !== "undefined" && window.innerWidth < 640 ? 6 : 8));
  const [storeLimit, setStoreLimit] = useState(() => (typeof window !== "undefined" && window.innerWidth < 640 ? 6 : 8));
  const [storeQuery, setStoreQuery] = useState("");
  const [settings, setSettings] = useState(() => ({
    brand_name: localStorage.getItem("brand_name") || "iStore",
    phone: localStorage.getItem("phone") || "077 600 97 97",
    hero_image_url: localStorage.getItem("hero") || DEFAULT_HERO,
    how_steps: [],
  }));
  const [featured, setFeatured] = useState([]);
  const [listings, setListings] = useState([]);
  const [isDesktop, setIsDesktop] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [storeTab, setStoreTab] = useState("all");

  useEffect(() => {
    api.get("/settings").then((r) => {
      setSettings((s) => ({ ...s, ...r.data }));
      if (r.data.hero_image_url) localStorage.setItem("hero", r.data.hero_image_url);
      if (r.data.brand_name) localStorage.setItem("brand_name", r.data.brand_name);
      if (r.data.phone) localStorage.setItem("phone", r.data.phone);
    }).catch(() => {});
    api.get("/featured").then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/listings").then((r) => setListings(r.data)).catch(() => {});
    api.get("/catalog").then((r) => setCatalog(r.data)).catch(() => {});
    if (!sessionStorage.getItem("visit_pinged")) {
      sessionStorage.setItem("visit_pinged", "1");
      api.post("/visit").catch(() => {});
    }
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const on = () => setIsDesktop(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const on = () => { const p = mq.matches ? 6 : 8; setPageSize(p); setStoreLimit(p); };
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  const openWizard = (init = null) => { setInitial(init); setWkey((k) => k + 1); setOpen(true); };
  const scrollToStore = () => document.getElementById("store")?.scrollIntoView({ behavior: "smooth" });
  const phoneTel = "tel:" + (settings.phone || "").replace(/[^\d+]/g, "");

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white antialiased overflow-x-clip">
      {/* HEADER */}
      <header className="sticky top-0 z-40 bg-[#0a0a0a]/85 backdrop-blur-xl border-b border-white/[0.06]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 h-14 lg:h-[60px] flex items-center justify-between">
          <div className="flex items-center" data-testid="nav-logo">
            <img src={fileUrl(LOGO)} alt="iStore" className="h-11 lg:h-8 w-auto object-contain object-left" />
          </div>
          <nav className="hidden lg:flex items-center gap-9 text-sm text-neutral-300">
            <button onClick={scrollToStore} className="hover:text-white transition-colors" data-testid="nav-store">Satışda</button>
            <button onClick={() => setHowOpen(true)} className="hover:text-white transition-colors" data-testid="nav-how">Necə işləyir?</button>
            <button onClick={() => setFaqOpen(true)} className="hover:text-white transition-colors" data-testid="nav-faq">FAQ</button>
            <a href={phoneTel} className="hover:text-white transition-colors" data-testid="nav-contact">Əlaqə</a>
          </nav>
          <div className="flex items-center gap-4 lg:gap-6">
            <a href={phoneTel} className="hidden md:flex flex-col items-end leading-tight" data-testid="nav-phone">
              <span className="text-[10px] uppercase tracking-widest text-neutral-500">Əlaqə</span>
              <span className="text-sm text-white font-medium">{settings.phone}</span>
            </a>
            <button onClick={() => openWizard()} data-testid="nav-sell-btn"
              className="hidden sm:inline-flex px-5 py-2.5 rounded-lg border border-white/20 text-sm hover:bg-white hover:text-black transition-colors">
              Satış et
            </button>
            <button onClick={scrollToStore} data-testid="nav-store-mobile"
              className="lg:hidden mr-12 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/15 bg-white/[0.04] text-xs font-medium text-neutral-200 hover:bg-white/10 transition-colors whitespace-nowrap">
              <Store className="w-3.5 h-3.5" /> Satışda
            </button>
            <button onClick={() => setMenuOpen((o) => !o)} data-testid="mobile-menu-toggle" aria-label="Menyu"
              className="lg:hidden w-11 h-11 rounded-xl border border-white/15 flex items-center justify-center text-white hover:bg-white/10 transition-colors">
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* MOBILE MENU */}
        {menuOpen && (
          <motion.nav initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}
            className="lg:hidden border-t border-white/[0.06] bg-[#0a0a0a]" data-testid="mobile-menu">
            <div className="px-6 py-3 flex flex-col text-[15px] text-neutral-200">
              <button onClick={() => { setMenuOpen(false); scrollToStore(); }} className="text-left py-3 border-b border-white/[0.05] hover:text-white transition-colors" data-testid="mobile-nav-store">Satışda</button>
              <button onClick={() => { setMenuOpen(false); setHowOpen(true); }} className="text-left py-3 border-b border-white/[0.05] hover:text-white transition-colors" data-testid="mobile-nav-how">Necə işləyir?</button>
              <button onClick={() => { setMenuOpen(false); setFaqOpen(true); }} className="text-left py-3 border-b border-white/[0.05] hover:text-white transition-colors" data-testid="mobile-nav-faq">FAQ</button>
              <a href={phoneTel} onClick={() => setMenuOpen(false)} className="py-3 border-b border-white/[0.05] hover:text-white transition-colors" data-testid="mobile-nav-contact">Əlaqə — {settings.phone}</a>
              <button onClick={() => { setMenuOpen(false); openWizard(); }} data-testid="mobile-nav-sell"
                className="mt-3 mb-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-white text-black text-sm font-medium hover:bg-neutral-200 transition-colors">
                Telefonunu sat <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </motion.nav>
        )}
      </header>

      {/* HERO — full-bleed background */}
      <section className="relative min-h-[460px] lg:min-h-[580px] overflow-hidden">
        <div className="absolute inset-0">
          {isDesktop && (
            <>
            <img src={fileUrl(settings.hero_image_url || DEFAULT_HERO)} alt="iPhone" className="w-full h-full object-contain object-[42%_center] select-none pointer-events-none" />
            <div className="absolute inset-y-0 left-0 w-2/5 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/70 to-transparent" />
            <div className="absolute inset-x-0 top-0 h-72 bg-gradient-to-b from-[#0a0a0a] via-[#0a0a0a]/85 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/80 to-transparent" />
            </>
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/10 to-[#0a0a0a]/55" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
          <div className="hidden lg:block absolute inset-0 bg-black/5" />
        </div>

        <div className="relative max-w-[1440px] mx-auto px-6 lg:px-10 pt-6 pb-12 lg:pt-16 lg:pb-20 grid lg:grid-cols-12 gap-8 items-start">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="lg:col-span-5">
            <div className="text-[11px] tracking-[0.25em] text-neutral-400 uppercase mb-6">Daha böyük imkanlar üçün</div>
            <h1 className="font-serif text-4xl sm:text-5xl xl:text-6xl leading-[1.08] tracking-tight">
              <span className="block whitespace-normal lg:whitespace-nowrap">Sənin iPhone‑un</span>
              <span className="block whitespace-normal lg:whitespace-nowrap text-neutral-400">hələ də dəyərlidir.</span>
            </h1>
            <p className="text-[15px] text-neutral-300 leading-relaxed mt-6 max-w-md">
              İstifadə etmədiyin iPhone-u asan, təhlükəsiz və sərfəli şəkildə bizə sat. Daha yaxşı bir texnologiya təcrübəsi üçün ilk addımı at.
            </p>
            <div className="flex items-center gap-8 mt-9 flex-wrap">
              <button onClick={() => openWizard()} data-testid="hero-sell-btn"
                className="inline-flex items-center gap-3 pl-6 pr-5 py-4 rounded-xl bg-gradient-to-b from-white to-neutral-200 text-black text-sm font-medium shadow-lg hover:to-white transition-colors">
                Telefonunu sat <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>

          <div className="hidden lg:block lg:col-span-3" />

          <motion.div id="estimator" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-4 scroll-mt-24">
            <QuickEstimator onEvaluate={openWizard} />
          </motion.div>
        </div>
      </section>

      {/* POPULAR MODELS */}
      <section className="border-t border-white/[0.06] bg-[#080808]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-20">
          <div className="flex flex-col lg:flex-row gap-10">
            <div className="lg:w-72 shrink-0">
              <div className="text-[11px] tracking-[0.25em] text-neutral-500 uppercase mb-5">Populyar modellər</div>
              <h2 className="font-serif text-4xl leading-tight">Hər nəsil<br />dəyərlidir.</h2>
              <p className="text-sm text-neutral-400 mt-5 max-w-xs">Köhnə model olsa belə, iPhone-un həmişə alıcısı var.</p>
            </div>

            <div className="flex-1 flex items-stretch gap-4 overflow-x-auto pb-4">
              {featured.map((m) => (
                <button key={m.id} onClick={() => openWizard(m.model_id ? { model_id: m.model_id, model_name: m.name, storage: null, condition: "like_new" } : null)}
                  data-testid={`popular-${m.model_id || m.id}`}
                  className="group text-left min-w-[210px] rounded-2xl border border-white/[0.07] bg-[#101010] hover:border-white/20 transition-colors overflow-hidden">
                  <div className="h-44 bg-[#0d0d0d] flex items-center justify-center overflow-hidden p-3">
                    {m.image_url
                      ? <img src={fileUrl(m.image_url)} alt={m.name} className="h-full w-full object-contain group-hover:scale-105 transition-transform duration-500" />
                      : <AppleLogo className="w-10 h-10 text-neutral-700" />}
                  </div>
                  <div className="p-4 flex items-end justify-between">
                    <div>
                      <div className="text-sm font-medium">{m.name}</div>
                      <div className="text-xs text-neutral-500 mt-0.5">{m.tagline}</div>
                    </div>
                    <span className="w-8 h-8 rounded-full border border-white/15 flex items-center justify-center shrink-0 group-hover:bg-white group-hover:text-black transition-colors">
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PHONES FOR SALE */}
      {listings.length > 0 && (
      <section id="store" className="border-t border-white/[0.06] bg-[#0a0a0a] scroll-mt-24">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-20">
          <div className="text-[11px] tracking-[0.25em] text-neutral-500 uppercase mb-3">Satışda</div>
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
            <div className="min-w-0">
              <h2 className="font-serif text-3xl sm:text-4xl leading-tight mb-3 break-words">Satışda olan</h2>
              <p className="text-sm text-neutral-400 max-w-lg">Yoxlanılmış, hazır cihazlar. Ətraflı məlumat üçün bizə zəng edin.</p>
              <div className="flex flex-wrap gap-2 mt-4">
                {[["all", "Hamısı"], ["phone", "iPhone"], ["tablet", "iPad"], ["watch", "Apple Watch"]].map(([k, l]) => (
                  <button key={k} onClick={() => { setStoreTab(k); setStoreLimit(pageSize); }} data-testid={`store-tab-${k}`}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${storeTab === k ? "bg-neutral-100 text-black" : "bg-white/5 text-neutral-400 hover:text-white"}`}>{l}</button>
                ))}
              </div>
            </div>
            <div className="relative w-full lg:w-72">
              <Search className="w-4 h-4 text-neutral-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input value={storeQuery} onChange={(e) => { setStoreQuery(e.target.value); setStoreLimit(pageSize); }}
                placeholder="Model, rəng, yaddaş axtar…" data-testid="store-search"
                className="w-full h-11 pl-10 pr-4 rounded-xl bg-white/[0.04] border border-white/10 text-base sm:text-sm text-neutral-200 placeholder:text-neutral-600 focus:outline-none focus:border-white/25 transition-colors" />
            </div>
          </div>

          {(() => {
            const norm = (s) => (s || "").toLowerCase().replace(/iphone/g, "").replace(/\s+/g, " ").trim();
            const q = norm(storeQuery);
            const raw = storeQuery.trim().toLowerCase();
            const byTab = storeTab === "all" ? listings : listings.filter((l) => (l.device_type || "phone") === storeTab);
            const filtered = q
              ? byTab.filter((l) => {
                  const modelExact = norm(l.name) === q;
                  const otherHit = `${l.color || ""} ${l.storage || ""} ${l.condition || ""}`.toLowerCase().includes(raw);
                  return modelExact || otherHit;
                })
              : byTab;
            const visible = filtered.slice(0, storeLimit);
            return (
              <>
                <div className="flex items-center justify-between mb-5">
                  <span className="text-xs text-neutral-500" data-testid="store-count">{filtered.length} nəticə</span>
                </div>
                {filtered.length === 0 ? (
                  <div className="text-sm text-neutral-500 py-16 text-center" data-testid="store-empty">Axtarışa uyğun telefon tapılmadı.</div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                      {visible.map((l) => (
                        <ListingCard key={l.id} listing={l} phoneTel={phoneTel} onZoom={(imgs, index) => { trackListingView(l.id); setLightbox({ images: imgs, index }); }} />
                      ))}
                    </div>
                    {filtered.length > storeLimit && (
                      <div className="flex justify-center mt-10">
                        <button onClick={() => setStoreLimit((n) => n + pageSize)} data-testid="store-load-more"
                          className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-white/15 text-sm text-neutral-200 hover:bg-white/5 transition-colors">
                          Daha çox göstər ({filtered.length - storeLimit})
                        </button>
                      </div>
                    )}
                  </>
                )}
              </>
            );
          })()}
        </div>
      </section>
      )}

      {/* FOOTER */}
      <footer className="border-t border-white/[0.06] bg-[#0a0a0a]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-3">
          <div className="grid gap-4 md:grid-cols-3">
            {/* brand */}
            <div className="space-y-2.5">
              <img src={fileUrl(LOGO)} alt="iStore" className="h-11 w-auto object-contain" />
              <p className="text-[13px] text-neutral-500 leading-relaxed max-w-xs">
                İşlənmiş iPhone-unuzu təhlükəsiz və sərfəli qiymətə satın. Yoxlanılmış cihazlar üçün etibarlı ünvan.
              </p>
              <a href={phoneTel} className="inline-flex items-center gap-2 text-sm text-neutral-300 hover:text-white transition-colors" data-testid="footer-phone">
                <Phone className="w-4 h-4" /> {settings.phone}
              </a>
            </div>

            {/* links */}
            <div className="md:justify-self-center">
              <div className="text-[10px] tracking-[0.22em] uppercase text-neutral-500 mb-2.5">Menyu</div>
              <ul className="space-y-1.5 text-sm text-neutral-400">
                <li><button onClick={scrollToStore} className="hover:text-white transition-colors" data-testid="footer-store">Satışda</button></li>
                <li><button onClick={() => setHowOpen(true)} className="hover:text-white transition-colors" data-testid="footer-how">Necə işləyir?</button></li>
                <li><button onClick={() => setFaqOpen(true)} className="hover:text-white transition-colors" data-testid="footer-faq">FAQ</button></li>
                <li><a href={phoneTel} className="hover:text-white transition-colors" data-testid="footer-contact">Əlaqə</a></li>
              </ul>
            </div>

            {/* cta */}
            <div className="md:justify-self-end space-y-2.5">
              <div className="text-[10px] tracking-[0.22em] uppercase text-neutral-500">Başlayaq</div>
              <p className="text-[13px] text-neutral-500 max-w-[220px]">Bir neçə dəqiqədə telefonunuzun dəyərini öyrənin.</p>
              <button onClick={() => openWizard()} data-testid="footer-sell-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white text-black text-sm font-medium hover:bg-neutral-200 transition-colors">
                Telefonunu sat <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="mt-4 pt-3 border-t border-white/[0.06] flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-neutral-600">
            <span>© {new Date().getFullYear()} iStore.az — Bütün hüquqlar qorunur.</span>
            <span>Bakı, Azərbaycan</span>
          </div>
        </div>
      </footer>

      {/* HOW IT WORKS DIALOG */}
      <Dialog open={howOpen} onOpenChange={setHowOpen}>
        <DialogContent className="max-w-lg bg-[#0d0d0d] border-white/10" data-testid="how-dialog">
          <DialogTitle className="font-serif text-2xl">Necə işləyir?</DialogTitle>
          <p className="text-sm text-neutral-400 -mt-1 mb-2">Qiymətləndirmə addım-addım belə baş verir.</p>
          <div className="space-y-4">
            {(settings.how_steps || []).map((s, i) => (
              <div key={i} className="flex gap-4" data-testid={`how-step-${i}`}>
                <span className="w-8 h-8 rounded-full border border-white/20 flex items-center justify-center text-sm shrink-0">{i + 1}</span>
                <div>
                  <div className="font-medium text-sm">{s.title}</div>
                  <div className="text-sm text-neutral-400 mt-0.5">{s.text}</div>
                </div>
              </div>
            ))}
          </div>
          <button onClick={() => { setHowOpen(false); openWizard(); }} data-testid="how-cta"
            className="mt-4 inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-neutral-100 text-black font-medium hover:bg-white transition-colors">
            Başla <ArrowRight className="w-4 h-4" />
          </button>
        </DialogContent>
      </Dialog>

      {/* FAQ DIALOG */}
      <Dialog open={faqOpen} onOpenChange={(o) => { setFaqOpen(o); if (!o) setFaqIdx(null); }}>
        <DialogContent className="max-w-lg bg-[#0d0d0d] border-white/10 max-h-[85vh] overflow-y-auto" data-testid="faq-dialog">
          <DialogTitle className="font-serif text-2xl">Tez-tez verilən suallar</DialogTitle>
          <div className="mt-2 border-y border-white/[0.06]">
            {[
              "Hansı iPhone-ları alırsınız?",
              "Yoxlama necə keçir?",
              "Zədəli telefonu satmaq olar?",
              "Ödəniş nağd, yoxsa köçürmə ilə?",
              "Bakıdan kənara gedirsiniz?",
            ].map((q, i) => (
              <div key={i} className={i > 0 ? "border-t border-white/[0.06]" : ""} data-testid={`faq-item-${i}`}>
                <button onClick={() => setFaqIdx((v) => (v === i ? null : i))} data-testid={`faq-q-${i}`}
                  className="w-full flex items-center justify-between gap-3 py-4 text-left group">
                  <span className="text-sm font-medium group-hover:text-white transition-colors">{q}</span>
                  <ChevronDown className={`w-4 h-4 shrink-0 text-neutral-400 transition-transform duration-300 ${faqIdx === i ? "rotate-180 text-white" : ""}`} />
                </button>
                <AnimatePresence initial={false}>
                  {faqIdx === i && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }} className="overflow-hidden" data-testid={`faq-a-${i}`}>
                      <div className="pb-4 text-sm text-neutral-400 leading-relaxed">
                        {i === 0 && (
                          <ul className="grid grid-cols-1 gap-1">
                            {catalog.map((m) => (
                              <li key={m.model_id} className="flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-neutral-600 shrink-0" /> {m.name}</li>
                            ))}
                          </ul>
                        )}
                        {i === 1 && (
                          <div className="space-y-2">
                            <p>Telefonun yoxlanılması şəffaf və sizin yanınızda həyata keçirilir.</p>
                            <ul className="space-y-1.5">
                              <li className="flex gap-2"><span className="text-neutral-600">•</span> Telefonun xarici görünüşü yoxlanılır.</li>
                              <li className="flex gap-2"><span className="text-neutral-600">•</span> Ekran, kamera, mikrofon, dinamik və düymələr test edilir.</li>
                              <li className="flex gap-2"><span className="text-neutral-600">•</span> Face ID / Touch ID, batareya və digər funksiyalar yoxlanılır.</li>
                              <li className="flex gap-2"><span className="text-neutral-600">•</span> iCloud hesabının çıxarılması və cihazın sıfırlanması yoxlanılır.</li>
                              <li className="flex gap-2"><span className="text-neutral-600">•</span> Yoxlamadan sonra yekun qiymət bildirilir. Razılaşdıqda ödəniş dərhal həyata keçirilir.</li>
                            </ul>
                          </div>
                        )}
                        {i === 2 && "Bəli. Telefon işləyirsə və tam hissə dəyişikliyi tələb etmirsə, onu almaqdan məmnun oluruq — cızıq, əzik və kiçik qüsurlar problem deyil."}
                        {i === 3 && "Sizə necə rahatdırsa — nağd və ya bank köçürməsi ilə ödəyirik."}
                        {i === 4 && "Xeyr, hazırda yalnız Bakı şəhəri daxilində alış edirik."}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* WIZARD DIALOG */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl w-[95vw] bg-[#0d0d0d] border-white/10 p-0 max-h-[92vh] overflow-y-auto">
          <div className="p-5 sm:p-7">
            <DialogTitle className="font-serif text-2xl mb-1">Telefonu qiymətləndir</DialogTitle>
            <p className="text-sm text-neutral-400 mb-6">Bir neçə addımda dəqiq təklif alın.</p>
            <Wizard key={wkey} initial={initial} />
          </div>
        </DialogContent>
      </Dialog>

      {/* LIGHTBOX GALLERY */}
      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="max-w-3xl w-[95vw] bg-[#0d0d0d] border-white/10 p-3" data-testid="lightbox">
          <DialogTitle className="sr-only">Şəkil</DialogTitle>
          {lightbox && (
            <div>
              <div className="relative bg-[#0a0a0a] rounded-xl flex items-center justify-center min-h-[50vh] max-h-[75vh] overflow-hidden">
                <img src={fileUrl(lightbox.images[lightbox.index])} alt="" className="max-h-[74vh] object-contain" />
                {lightbox.images.length > 1 && (
                  <>
                    <button onClick={() => setLightbox((s) => ({ ...s, index: (s.index - 1 + s.images.length) % s.images.length }))}
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 flex items-center justify-center hover:bg-black/80 transition-colors"><ChevronLeft className="w-5 h-5" /></button>
                    <button onClick={() => setLightbox((s) => ({ ...s, index: (s.index + 1) % s.images.length }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 flex items-center justify-center hover:bg-black/80 transition-colors"><ChevronRight className="w-5 h-5" /></button>
                  </>
                )}
              </div>
              {lightbox.images.length > 1 && (
                <div className="flex gap-2 mt-3 justify-center flex-wrap">
                  {lightbox.images.map((u, i) => (
                    <button key={i} onClick={() => setLightbox((s) => ({ ...s, index: i }))}
                      className={`w-14 h-14 rounded-lg overflow-hidden border transition-colors ${i === lightbox.index ? "border-white" : "border-white/10"}`}>
                      <img src={fileUrl(u)} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
